import React from 'react';
import { Link } from 'react-router-dom';

const CICD = [
  { icon: '⚙️', name: 'GitHub Actions',  desc: 'Native action: scan SBOMs, enforce policy gates, and post findings as PR comments.', badge: 'Official', accent: '#6366f1' },
  { icon: '☁️', name: 'Azure DevOps',     desc: 'YAML task for Azure Pipelines. Integrates with Azure Repos for automated SBOM scanning.', badge: 'Official', accent: '#0078d4' },
  { icon: '🦊', name: 'GitLab CI',        desc: 'Add DepGuard as a pipeline stage. Block MRs on policy violations with SARIF output.', badge: 'Coming soon', accent: '#fc6d26' },
  { icon: '🔧', name: 'Jenkins',          desc: 'Plugin-free setup via the depguard CLI. Works in any shell step.', badge: 'Coming soon', accent: '#d33833' },
  { icon: '🦊', name: 'Bitbucket Pipelines', desc: 'Pipe step available. Scan on every push and block on critical CVEs.', badge: 'Coming soon', accent: '#0052cc' },
];

const ALERTS = [
  { icon: '💬', name: 'Slack',         desc: 'Get a Slack alert the moment a new CRITICAL CVE affects one of your SBOMs. Configure per-channel routing by severity.', accent: '#4a154b' },
  { icon: '👥', name: 'Microsoft Teams', desc: 'Adaptive card alerts for new vulnerabilities, policy failures, and abandoned packages. Supports channel webhooks.', accent: '#6264a7' },
  { icon: '📧', name: 'Email',          desc: 'Weekly digest or real-time alerts. Configurable thresholds, recipients, and cadence.', accent: '#6366f1', coming: true },
  { icon: '🔗', name: 'Custom Webhooks', desc: 'HMAC-signed JSON payloads for any system. 4 event types: new_vuln, policy_fail, abandoned, scan_complete.', accent: '#334155' },
];

const TICKETING = [
  { icon: '🐙', name: 'GitHub Issues', desc: 'Create GitHub Issues directly from remediation items. Two-way sync: closing the issue resolves the remediation automatically. Supports GitHub.com and GitHub Enterprise Server.', status: 'Live', accent: '#24292f' },
  { icon: '📋', name: 'Jira',          desc: 'Convert remediation items into Jira tickets in one click. Two-way status sync keeps DepGuard and Jira aligned without manual updates.', status: 'Live', accent: '#0052cc' },
  { icon: '📌', name: 'Azure Boards',  desc: 'Create work items in Azure DevOps directly from DepGuard findings. Linked to SBOM and CVE for full traceability from finding to fix.', status: 'Coming soon', accent: '#0078d4' },
];

const SOURCES = [
  { icon: '📦', name: 'Syft (Anchore)', desc: 'Generate SBOMs from your source code, Docker images, or directories using Syft, then upload to DepGuard.' },
  { icon: '📤', name: 'Manual upload',  desc: 'Drag-and-drop or API upload of SPDX-JSON and CycloneDX-JSON SBOMs from any tool.' },
  { icon: '🔄', name: 'Azure DevOps repos', desc: 'Connect your Azure DevOps organisation. DepGuard discovers repos and auto-generates SBOMs on schedule.' },
  { icon: '🌐', name: 'OSV.dev',        desc: 'All vulnerability data sourced from Google\'s open OSV database — no API key required.' },
  { icon: '📊', name: 'deps.dev',       desc: 'Package health, license data, and deprecation signals from Google deps.dev.' },
  { icon: '🏆', name: 'OpenSSF Scorecard', desc: 'Security posture score for every GitHub-hosted package in your SBOM.' },
];

const FRAMEWORKS = [
  { name: 'EU Cyber Resilience Act', desc: 'Article 20 field validation, compliance percentage, downloadable audit document.' },
  { name: 'NIST SP 800-53',         desc: 'Control mapping for SA-10, SA-11, SI-2, SI-3, SR-3 and more.' },
  { name: 'ISO 27001',              desc: 'Annex A.12 mapping for software supply chain requirements.' },
  { name: 'SLSA Level 1–3',         desc: 'Build provenance verification and attestation support.' },
  { name: 'SPDX 2.2/2.3',          desc: 'Native SPDX-JSON import and export.' },
  { name: 'CycloneDX 1.4–1.6',     desc: 'Native CycloneDX-JSON + VEX export.' },
];

function SectionHeader({ tag, title, lead }) {
  return (
    <div className="dgw-section-header" style={{marginBottom:48}}>
      <span className="dgw-section-tag">{tag}</span>
      <h2>{title}</h2>
      {lead && <p className="dgw-section-lead">{lead}</p>}
    </div>
  );
}

export default function WebsiteIntegrationsPage() {
  return (
    <div className="dgw-page-wrap">

      {/* Hero */}
      <section className="dgw-hero" style={{textAlign:'center'}}>
        <div className="dgw-hero-inner" style={{maxWidth:820}}>
          <div className="dgw-badge">
            <span className="dgw-badge-dot" />
            Integrations
          </div>
          <h1>Fits where you<br/>already work.</h1>
          <p className="dgw-hero-lead" style={{maxWidth:600, margin:'0 auto 40px'}}>
            DepGuard plugs into your existing CI/CD pipelines, alert channels, and ticketing systems.
            No new dashboard to check. Results go where your team already looks.
          </p>
          <div className="dgw-hero-actions" style={{justifyContent:'center'}}>
            <Link to="/trial" className="dgw-btn dgw-btn-primary dgw-btn-lg">Start integrating →</Link>
            <Link to="/website/whitepaper" className="dgw-btn dgw-btn-ghost dgw-btn-lg">Read the docs</Link>
          </div>
        </div>
      </section>

      {/* CI/CD */}
      <section className="dgw-section" style={{background:'#fff'}}>
        <div style={{maxWidth:1100, margin:'0 auto'}}>
          <SectionHeader tag="CI/CD" title="Block non-compliant builds before they ship."
            lead="One pipeline step. Set a policy (no critical CVEs, no GPL licenses, no abandoned packages) and the build fails before it reaches production." />
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))', gap:20}}>
            {CICD.map(ci => (
              <div key={ci.name} style={{
                padding:'24px', borderRadius:14,
                background:'#f8fafc', border:'1px solid #e2e8f0',
                display:'flex', flexDirection:'column', gap:12,
              }}>
                <div style={{display:'flex', alignItems:'center', gap:12, justifyContent:'space-between'}}>
                  <div style={{display:'flex', alignItems:'center', gap:10}}>
                    <span style={{fontSize:24}}>{ci.icon}</span>
                    <span style={{fontSize:15, fontWeight:700, color:'#0f172a'}}>{ci.name}</span>
                  </div>
                  <span style={{
                    fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:100,
                    background: ci.badge === 'Official' ? 'rgba(99,102,241,.1)' : ci.badge === 'Coming soon' ? 'rgba(245,158,11,.1)' : 'rgba(148,163,184,.12)',
                    color: ci.badge === 'Official' ? '#6366f1' : ci.badge === 'Coming soon' ? '#b45309' : '#64748b',
                    border: ci.badge === 'Official' ? '1px solid rgba(99,102,241,.25)' : ci.badge === 'Coming soon' ? '1px solid rgba(245,158,11,.25)' : '1px solid #e2e8f0',
                    whiteSpace:'nowrap',
                  }}>{ci.badge}</span>
                </div>
                <p style={{fontSize:13.5, color:'#475569', lineHeight:1.6, margin:0}}>{ci.desc}</p>
              </div>
            ))}
          </div>

          {/* GitHub Actions example */}
          <div style={{marginTop:48}}>
            <div style={{fontSize:13, fontWeight:700, color:'#64748b', marginBottom:12, textTransform:'uppercase', letterSpacing:.5}}>Example: GitHub Actions</div>
            <div style={{
              borderRadius:14, overflow:'hidden', fontFamily:'monospace',
              fontSize:13, lineHeight:1.8,
            }}>
              <div style={{background:'#1e293b', padding:'10px 18px', display:'flex', alignItems:'center', gap:8}}>
                <span style={{color:'#64748b', fontSize:11}}>/.github/workflows/depguard.yml</span>
              </div>
              <div style={{background:'#0f172a', padding:'20px 24px', color:'#e2e8f0'}}>
                <div><span style={{color:'#7dd3fc'}}>name</span><span style={{color:'#94a3b8'}}>: </span>DepGuard SBOM Scan</div>
                <div><span style={{color:'#7dd3fc'}}>on</span><span style={{color:'#94a3b8'}}>: </span>[push, pull_request]</div>
                <div style={{marginTop:8}}><span style={{color:'#7dd3fc'}}>jobs</span><span style={{color:'#94a3b8'}}>:</span></div>
                <div style={{paddingLeft:16}}><span style={{color:'#7dd3fc'}}>scan</span><span style={{color:'#94a3b8'}}>:</span></div>
                <div style={{paddingLeft:32}}><span style={{color:'#7dd3fc'}}>runs-on</span><span style={{color:'#94a3b8'}}>: </span>ubuntu-latest</div>
                <div style={{paddingLeft:32, marginTop:4}}><span style={{color:'#7dd3fc'}}>steps</span><span style={{color:'#94a3b8'}}>:</span></div>
                <div style={{paddingLeft:48}}><span style={{color:'#94a3b8'}}>- </span><span style={{color:'#7dd3fc'}}>uses</span><span style={{color:'#94a3b8'}}>: </span>lexint/depguard-action@v2</div>
                <div style={{paddingLeft:56}}><span style={{color:'#7dd3fc'}}>with</span><span style={{color:'#94a3b8'}}>:</span></div>
                <div style={{paddingLeft:68}}><span style={{color:'#7dd3fc'}}>sbom</span><span style={{color:'#94a3b8'}}>: </span><span style={{color:'#86efac'}}>./sbom.cdx.json</span></div>
                <div style={{paddingLeft:68}}><span style={{color:'#7dd3fc'}}>policy</span><span style={{color:'#94a3b8'}}>: </span><span style={{color:'#86efac'}}>no-critical-cve</span></div>
                <div style={{paddingLeft:68}}><span style={{color:'#7dd3fc'}}>fail-on</span><span style={{color:'#94a3b8'}}>: </span><span style={{color:'#86efac'}}>critical</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alerts */}
      <section className="dgw-section" style={{background:'#f8fafc'}}>
        <div style={{maxWidth:1100, margin:'0 auto'}}>
          <SectionHeader tag="Alerting" title="Alerts reach your team. Not a dashboard they check weekly."
            lead="New CRITICAL CVE in a production dependency? Your team knows in seconds, in Slack or Teams, with full context attached." />
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:20}}>
            {ALERTS.map(a => (
              <div key={a.name} style={{
                padding:'24px', borderRadius:14,
                background:'#fff', border:'1px solid #e2e8f0',
                display:'flex', flexDirection:'column', gap:10,
              }}>
                <span style={{fontSize:28}}>{a.icon}</span>
                <div style={{display:'flex', alignItems:'center', gap:8}}>
                  <span style={{fontSize:15, fontWeight:700, color:'#0f172a'}}>{a.name}</span>
                  {a.coming && <span style={{fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:100, background:'rgba(245,158,11,.1)', color:'#b45309', border:'1px solid rgba(245,158,11,.25)'}}>Coming soon</span>}
                </div>
                <p style={{fontSize:13.5, color:'#475569', lineHeight:1.6, margin:0}}>{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ticketing */}
      <section className="dgw-section" style={{background:'#fff'}}>
        <div style={{maxWidth:1100, margin:'0 auto'}}>
          <SectionHeader tag="Ticketing" title="Findings become tickets. No copy-paste."
            lead="DepGuard remediations sync to Jira or GitHub Issues with the CVE, package, severity, and affected repo already attached." />
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:20}}>
            {TICKETING.map(t => (
              <div key={t.name} style={{
                padding:'24px', borderRadius:14,
                background:'#f8fafc', border:'1px solid #e2e8f0',
                display:'flex', flexDirection:'column', gap:10, position:'relative',
              }}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                  <span style={{fontSize:28}}>{t.icon}</span>
                  <span style={{
                    fontSize:11, fontWeight:700, padding:'4px 10px', borderRadius:100,
                    background: t.status === 'Live' ? 'rgba(16,185,129,.1)' : 'rgba(245,158,11,.1)',
                    color: t.status === 'Live' ? '#065f46' : '#b45309',
                    border: t.status === 'Live' ? '1px solid rgba(16,185,129,.25)' : '1px solid rgba(245,158,11,.25)',
                  }}>{t.status}</span>
                </div>
                <span style={{fontSize:15, fontWeight:700, color:'#0f172a'}}>{t.name}</span>
                <p style={{fontSize:13.5, color:'#475569', lineHeight:1.6, margin:0}}>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data sources */}
      <section className="dgw-section" style={{background:'#0f172a'}}>
        <div style={{maxWidth:1100, margin:'0 auto'}}>
          <div className="dgw-section-header" style={{marginBottom:48}}>
            <span className="dgw-section-tag" style={{background:'rgba(99,102,241,.15)', color:'#a5b4fc', borderColor:'rgba(99,102,241,.3)'}}>Data sources</span>
            <h2 style={{color:'#f1f5f9'}}>Open data. No vendor lock-in.</h2>
            <p className="dgw-section-lead" style={{color:'#94a3b8'}}>
              Every intelligence source is open and independently verifiable. No opaque scoring. No proprietary feeds you can't audit.
            </p>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:16}}>
            {SOURCES.map(s => (
              <div key={s.name} style={{
                padding:'20px 24px', borderRadius:12,
                background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)',
                display:'flex', gap:14, alignItems:'flex-start',
              }}>
                <span style={{fontSize:20, flexShrink:0, marginTop:2}}>{s.icon}</span>
                <div>
                  <div style={{fontSize:14, fontWeight:700, color:'#f1f5f9', marginBottom:4}}>{s.name}</div>
                  <div style={{fontSize:13, color:'#64748b', lineHeight:1.5}}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance frameworks */}
      <section className="dgw-section" style={{background:'#fff'}}>
        <div style={{maxWidth:1100, margin:'0 auto'}}>
          <SectionHeader tag="Standards & compliance" title="Every major framework. No spreadsheet mapping."
            lead="DepGuard findings map directly to compliance controls. Your auditor gets a report, not a list of screenshots." />
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:16}}>
            {FRAMEWORKS.map(f => (
              <div key={f.name} style={{
                padding:'20px', borderRadius:12,
                background:'#f8fafc', border:'1px solid #e2e8f0',
              }}>
                <div style={{fontSize:13, fontWeight:700, color:'#6366f1', marginBottom:6}}>{f.name}</div>
                <div style={{fontSize:13, color:'#475569', lineHeight:1.5}}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="dgw-cta-band">
        <div className="dgw-cta-band-inner">
          <h2>Set up GitHub Actions in 10 minutes.</h2>
          <p>One YAML step. SBOM scanned, policy enforced, findings posted as PR annotations.</p>
          <div className="dgw-cta-band-actions">
            <Link to="/trial" className="dgw-btn-cta-white">Start free trial</Link>
            <Link to="/website/contact" className="dgw-btn-cta-ghost">Talk to an engineer</Link>
          </div>
        </div>
      </section>

    </div>
  );
}
